'use client';

import React, { useState } from 'react';
import {
  Box,
  Card,
  TextField,
  Button,
  Typography,
  Link,
  Snackbar,
  Alert,
  CircularProgress,
  InputAdornment,
  IconButton,
  LinearProgress,
  Checkbox,
  FormControlLabel,
  FormControl,
  FormHelperText,
} from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { useForm, Controller } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { GoogleSignInButton, GoogleSignInDivider } from './GoogleSignInButton';

// Compute whether a YYYY-MM-DD date of birth is at least 18 years ago.
const isAtLeast18 = (dob: string): boolean => {
  if (!dob) return false;
  const d = new Date(dob);
  if (Number.isNaN(d.getTime())) return false;
  const today = new Date();
  const eighteenth = new Date(d.getFullYear() + 18, d.getMonth(), d.getDate());
  return eighteenth <= today;
};

// Validation schema
const SignupSchema = z
  .object({
    email: z.string().email('Invalid email format'),
    password: z
      .string()
      .min(8, 'Password must be at least 8 characters')
      .regex(/[A-Z]/, 'Password must contain at least one uppercase letter')
      .regex(/[a-z]/, 'Password must contain at least one lowercase letter')
      .regex(/[0-9]/, 'Password must contain at least one number'),
    confirmPassword: z.string(),
    username: z
      .string()
      .min(3, 'Username must be at least 3 characters')
      .max(20, 'Username must be at most 20 characters')
      .regex(/^[a-zA-Z0-9_]+$/, 'Username must be alphanumeric with underscores only'),
    displayName: z
      .string()
      .min(1, 'Display name is required')
      .max(50, 'Display name must be at most 50 characters'),
    dateOfBirth: z
      .string()
      .min(1, 'Date of birth is required')
      .refine((dob: string) => isAtLeast18(dob), 'You must be at least 18 years old to use Percoro'),
    acceptedTermsPrivacyAge: z
      .boolean()
      .refine((v: boolean) => v === true, {
        message: 'You must confirm your age and accept the Terms of Use and Privacy Policy',
      }),
    acceptedAup: z
      .boolean()
      .refine((v: boolean) => v === true, {
        message: 'You must accept the Acceptable Use Policy',
      }),
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: 'Passwords do not match',
    path: ['confirmPassword'],
  });

type SignupFormData = z.infer<typeof SignupSchema>;

interface SignupSuccessResponse {
  success: true;
  userId: string;
  message: string;
  needsConfirmation?: boolean;
}

interface SignupErrorResponse {
  success: false;
  error: string;
  details?: Array<{ path: (string | number)[]; message: string }>;
}

type SignupResponse = SignupSuccessResponse | SignupErrorResponse;

const todayStr = new Date().toISOString().slice(0, 10);

const tealCheckbox = { color: '#2D6E6A', '&.Mui-checked': { color: '#2D6E6A' } };

/**
 * Signup form component with Material-UI
 * Validates user input and calls signup API
 */
export const SignupForm: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [emailExists, setEmailExists] = useState(false);

  // Get redirect URL from query params
  const redirectUrl = searchParams.get('redirect') || '/explore';

  const {
    control,
    handleSubmit,
    formState: { errors },
    watch,
    reset,
  } = useForm<SignupFormData>({
    resolver: zodResolver(SignupSchema),
    mode: 'onChange',
    defaultValues: {
      dateOfBirth: '',
      acceptedTermsPrivacyAge: false,
      acceptedAup: false,
    },
  });

  const password = watch('password');
  const dateOfBirth = watch('dateOfBirth');
  const acceptedTermsPrivacyAge = watch('acceptedTermsPrivacyAge');
  const acceptedAup = watch('acceptedAup');

  const getPasswordStrength = (): { strength: number; color: 'error' | 'warning' | 'success' } => {
    if (!password) return { strength: 0, color: 'error' };
    let strength = 0;
    if (password.length >= 8) strength += 1;
    if (password.length >= 12) strength += 1;
    if (/[A-Z]/.test(password) && /[a-z]/.test(password) && /[0-9]/.test(password)) strength += 1;
    if (/[!@#$%^&*]/.test(password)) strength += 1;
    return {
      strength: Math.min(strength * 25, 100),
      color: strength <= 1 ? 'error' : strength <= 2 ? 'warning' : 'success',
    };
  };

  const passwordStrength = getPasswordStrength();

  const onSubmit = async (data: SignupFormData) => {
    setLoading(true);
    setErrorMessage('');
    setSuccessMessage('');
    setEmailExists(false);

    let response: Response;
    try {
      response = await fetch('https://3yslsbk5n1.execute-api.us-east-1.amazonaws.com/prod/auth/signup', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: data.email,
          password: data.password,
          username: data.username,
          displayName: data.displayName,
          dateOfBirth: data.dateOfBirth,
          acceptedTermsPrivacyAge: data.acceptedTermsPrivacyAge,
          acceptedAup: data.acceptedAup,
        }),
      });
    } catch {
      setErrorMessage("Couldn't reach Percoro. Check your connection and try again.");
      setLoading(false);
      return;
    }

    try {
      const result: SignupResponse = await response.json();

      if (!response.ok || result.success === false) {
        const errorResult = result as SignupErrorResponse;
        const backendError = errorResult.error || '';

        if (backendError === 'Username already exists') {
          setErrorMessage("That username's taken — try another.");
        } else if (backendError === 'Email already registered') {
          // Stay on signup. Show inline alert with login + reset-password options.
          setEmailExists(true);
        } else if (backendError === 'Password does not meet requirements') {
          setErrorMessage("That password doesn't meet the requirements listed above.");
        } else if (backendError === 'Validation failed') {
          const firstDetail = errorResult.details?.[0]?.message;
          setErrorMessage(firstDetail ? `Please check: ${firstDetail}` : 'Some of the info entered is not valid.');
        } else if (response.status >= 500) {
          setErrorMessage('Something went wrong on our end. Try again in a moment?');
        } else {
          setErrorMessage(backendError || 'Signup did not complete. Try again.');
        }
        return;
      }

      setSuccessMessage('Account created! Check your email for a verification code.');
      reset();
      setTimeout(() => {
        const confirmUrl = redirectUrl !== '/explore'
          ? `/confirm-signup?email=${encodeURIComponent(data.email)}&redirect=${encodeURIComponent(redirectUrl)}`
          : `/confirm-signup?email=${encodeURIComponent(data.email)}`;
        navigate(confirmUrl);
      }, 2000);
    } catch (error) {
      console.error('Signup unexpected error:', error);
      setErrorMessage('Something went wrong. Try again in a moment?');
    } finally {
      setLoading(false);
    }
  };

  const handleTogglePasswordVisibility = () => setShowPassword(!showPassword);
  const handleToggleConfirmPasswordVisibility = () => setShowConfirmPassword(!showConfirmPassword);

  // Build login URL preserving redirect param
  const loginUrl = redirectUrl !== '/explore'
    ? `/login?redirect=${encodeURIComponent(redirectUrl)}`
    : '/login';

  const consentComplete = !!dateOfBirth && acceptedTermsPrivacyAge === true && acceptedAup === true;

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        padding: 2,
      }}
    >
      <Card
        sx={{
          width: '100%',
          maxWidth: 500,
          padding: 4,
          boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
        }}
      >
        <Typography
          variant="h4"
          component="h1"
          onClick={() => navigate('/')}
          sx={{
            marginBottom: 1,
            fontWeight: 700,
            textAlign: 'center',
            color: '#2D6E6A',
            cursor: 'pointer',
            '&:hover': { color: '#2D6E6A' },
          }}
        >
          Join Percoro
        </Typography>
        <Typography
          variant="body2"
          sx={{
            textAlign: 'center',
            color: '#666',
            marginBottom: 3,
          }}
        >
          Create an account to start planning your adventures
        </Typography>

        {emailExists && (
          <Alert severity="warning" onClose={() => setEmailExists(false)} sx={{ marginBottom: 2 }}>
            An account with this email already exists. You can{' '}
            <Link href="/login" sx={{ fontWeight: 600, cursor: 'pointer' }}>log in</Link>
            {', '}
            <Link href="/forgot-password" sx={{ fontWeight: 600, cursor: 'pointer' }}>reset your password</Link>
            , or change the email above and try again.
          </Alert>
        )}

        <GoogleSignInButton label="Sign up with Google" />
        <GoogleSignInDivider />

        <form onSubmit={handleSubmit(onSubmit)}>
          {/* Email Field */}
          <Controller
            name="email"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                fullWidth
                label="Email Address"
                type="email"
                variant="outlined"
                margin="normal"
                error={!!errors.email}
                helperText={errors.email?.message}
                disabled={loading}
              />
            )}
          />

          {/* Username Field */}
          <Controller
            name="username"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                fullWidth
                label="Username"
                variant="outlined"
                margin="normal"
                error={!!errors.username}
                helperText={errors.username?.message || 'Alphanumeric with underscores (3-20 chars)'}
                disabled={loading}
              />
            )}
          />

          {/* Display Name Field */}
          <Controller
            name="displayName"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                fullWidth
                label="Display Name"
                variant="outlined"
                margin="normal"
                error={!!errors.displayName}
                helperText={errors.displayName?.message}
                disabled={loading}
              />
            )}
          />

          {/* Date of Birth Field (18+ gate) */}
          <Controller
            name="dateOfBirth"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                fullWidth
                label="Date of Birth"
                type="date"
                variant="outlined"
                margin="normal"
                InputLabelProps={{ shrink: true }}
                inputProps={{ max: todayStr }}
                error={!!errors.dateOfBirth}
                helperText={errors.dateOfBirth?.message || 'You must be 18 or older to use Percoro'}
                disabled={loading}
              />
            )}
          />

          {/* Password Field */}
          <Controller
            name="password"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                fullWidth
                label="Password"
                type={showPassword ? 'text' : 'password'}
                variant="outlined"
                margin="normal"
                error={!!errors.password}
                helperText={errors.password?.message}
                disabled={loading}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton onClick={handleTogglePasswordVisibility} edge="end" disabled={loading}>
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            )}
          />

          {/* Password Strength Indicator */}
          {password && (
            <Box sx={{ marginY: 1 }}>
              <LinearProgress variant="determinate" value={passwordStrength.strength} color={passwordStrength.color} />
              <Typography variant="caption" color={passwordStrength.color}>
                {passwordStrength.strength < 50
                  ? 'Weak'
                  : passwordStrength.strength < 75
                  ? 'Moderate'
                  : 'Strong'}{' '}
                password
              </Typography>
            </Box>
          )}

          {/* Confirm Password Field */}
          <Controller
            name="confirmPassword"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                fullWidth
                label="Confirm Password"
                type={showConfirmPassword ? 'text' : 'password'}
                variant="outlined"
                margin="normal"
                error={!!errors.confirmPassword}
                helperText={errors.confirmPassword?.message}
                disabled={loading}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton onClick={handleToggleConfirmPasswordVisibility} edge="end" disabled={loading}>
                        {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            )}
          />

          {/* Password Requirements */}
          <Typography variant="caption" sx={{ display: 'block', marginTop: 2, color: '#666' }}>
            Password must contain:
            <ul style={{ margin: '4px 0', paddingLeft: 20 }}>
              <li>At least 8 characters</li>
              <li>One uppercase letter</li>
              <li>One lowercase letter</li>
              <li>One number</li>
            </ul>
          </Typography>

          {/* Consent — Box 1: age + accuracy + Terms/Privacy */}
          <Controller
            name="acceptedTermsPrivacyAge"
            control={control}
            render={({ field }) => (
              <FormControl error={!!errors.acceptedTermsPrivacyAge} component="fieldset" sx={{ mt: 2, display: 'block' }}>
                <FormControlLabel
                  sx={{ alignItems: 'flex-start', m: 0 }}
                  control={
                    <Checkbox
                      checked={!!field.value}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => field.onChange(e.target.checked)}
                      disabled={loading}
                      sx={{ ...tealCheckbox, pt: 0 }}
                    />
                  }
                  label={
                    <Typography variant="body2" sx={{ color: '#444' }}>
                      I confirm that I am at least 18 years old and that the date of birth I entered is accurate. I have read and agree to the{' '}
                      <Link href="/terms" target="_blank" rel="noopener" sx={{ fontWeight: 600 }}>Terms of Use</Link>
                      {' '}and{' '}
                      <Link href="/privacy" target="_blank" rel="noopener" sx={{ fontWeight: 600 }}>Privacy Policy</Link>.
                    </Typography>
                  }
                />
                {errors.acceptedTermsPrivacyAge && (
                  <FormHelperText>{errors.acceptedTermsPrivacyAge.message}</FormHelperText>
                )}
              </FormControl>
            )}
          />

          {/* Consent — Box 2: Acceptable Use Policy (separate, explicit) */}
          <Controller
            name="acceptedAup"
            control={control}
            render={({ field }) => (
              <FormControl error={!!errors.acceptedAup} component="fieldset" sx={{ mt: 1.5, display: 'block' }}>
                <FormControlLabel
                  sx={{ alignItems: 'flex-start', m: 0 }}
                  control={
                    <Checkbox
                      checked={!!field.value}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => field.onChange(e.target.checked)}
                      disabled={loading}
                      sx={{ ...tealCheckbox, pt: 0 }}
                    />
                  }
                  label={
                    <Typography variant="body2" sx={{ color: '#444' }}>
                      I agree to the{' '}
                      <Link href="/aup" target="_blank" rel="noopener" sx={{ fontWeight: 600 }}>Acceptable Use Policy</Link>
                      {' '}and understand that pornographic, sexually explicit, abusive, harassing, or exploitative content is strictly prohibited and may result in content removal and account termination.
                    </Typography>
                  }
                />
                {errors.acceptedAup && <FormHelperText>{errors.acceptedAup.message}</FormHelperText>}
              </FormControl>
            )}
          />

          {/* Submit Button — blocked until DOB + both consents are satisfied */}
          <Button
            type="submit"
            fullWidth
            variant="contained"
            color="primary"
            sx={{
              marginTop: 3,
              padding: '12px 0',
              fontSize: '1rem',
              fontWeight: 600,
            }}
            disabled={loading || !consentComplete}
            endIcon={loading ? <CircularProgress size={24} /> : undefined}
          >
            {loading ? 'Creating Account...' : 'Create Account'}
          </Button>

          {/* Login Link */}
          <Box sx={{ textAlign: 'center', marginTop: 2 }}>
            <Typography variant="body2">
              Already have an account?{' '}
              <Link href={loginUrl} sx={{ fontWeight: 600, cursor: 'pointer' }}>
                Login here
              </Link>
            </Typography>
          </Box>
        </form>
      </Card>

      {/* Success Snackbar */}
      <Snackbar
        open={!!successMessage}
        autoHideDuration={6000}
        onClose={() => setSuccessMessage('')}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={() => setSuccessMessage('')} severity="success" sx={{ width: '100%' }}>
          {successMessage}
        </Alert>
      </Snackbar>

      {/* Error Snackbar */}
      <Snackbar
        open={!!errorMessage}
        autoHideDuration={6000}
        onClose={() => setErrorMessage('')}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={() => setErrorMessage('')} severity="error" sx={{ width: '100%' }}>
          {errorMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default SignupForm;
